import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../services/api';
import {
  PhoneIcon,
  PlusIcon,
  EditIcon,
  TrashIcon,
  AlertCircleIcon,
  CheckCircleIcon,
  XCircleIcon,
  EyeIcon,
  EyeOffIcon,
  RefreshCwIcon,
  SettingsIcon,
  CopyIcon,
  CheckIcon,
} from 'lucide-react';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import ContractServiceModal from '../../components/ui/ContractServiceModal';
import { ConfigureServiceModal } from '../../components/ui/ConfigureServiceModal';
import ConfigureBusinessHoursModal from '../../components/ui/ConfigureBusinessHoursModal';

interface Service {
  id: number;
  extension: string;
  password: string;
  name: string;
  description?: string;
  status: 'active' | 'inactive' | 'suspended';
  server_id: number;
  plan_id?: number;
  client_secret?: string;
  phone_id?: string;
  meta_api_key?: string;
  whmcs_service_id?: number;
  whmcs_order_id?: number;
  whmcs_invoice_id?: number;
  whmcs_status?: 'pending' | 'active' | 'suspended' | 'terminated' | 'cancelled';
  invoice_status?: string | null; // Status da fatura no WHMCS
  billing_cycle?: 'monthly' | 'quarterly' | 'semiannually' | 'annually';
  next_due_date?: string;
  created_at: string;
  server: {
    id: number;
    name: string;
    url: string;
  };
  plan?: {
    id: number;
    name: string;
    price: number;
    concurrent_calls: number;
    monthly_calls: number | null;
    features?: string[];
  };
}

interface Plan {
  id: number;
  name: string;
  price: number;
  concurrent_calls: number;
  monthly_calls: number | null;
  description?: string;
  features?: string[];
  whmcs_product_id?: number;
  is_whmcs_product?: boolean;
}

const ServicesPage: React.FC = () => {
  const navigate = useNavigate();
  const [services, setServices] = useState<Service[]>([]);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [isLoadingPlans, setIsLoadingPlans] = useState(false);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 8;
  const [showEditModal, setShowEditModal] = useState(false);
  const [showConfigureModal, setShowConfigureModal] = useState(false);
  const [selectedService, setSelectedService] = useState<Service | null>(null);
  const [showPasswordFor, setShowPasswordFor] = useState<number | null>(null);
  const [isCreatingService, setIsCreatingService] = useState(false);
  const [editService, setEditService] = useState({
    name: '',
    description: '',
  });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [serviceToDelete, setServiceToDelete] = useState<Service | null>(null);
  const [deleteConfirmationName, setDeleteConfirmationName] = useState('');
  const [isDeletingService, setIsDeletingService] = useState(false);
  const [syncingId, setSyncingId] = useState<number | null>(null);
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [checkingMetaStatus, setCheckingMetaStatus] = useState<number | null>(null);
  const [metaStatus, setMetaStatus] = useState<{[key: number]: {configured: boolean, message: string, details?: any}}>({});
  const [showBusinessHoursModal, setShowBusinessHoursModal] = useState(false);
  const [checkingBusinessHoursStatus, setCheckingBusinessHoursStatus] = useState<number | null>(null);
  const [businessHoursStatus, setBusinessHoursStatus] = useState<{[key: number]: {configured: boolean, message: string, details?: any}}>({});
  const [preloadedBusinessHours, setPreloadedBusinessHours] = useState<{[key: number]: any}>({});

  // Função para copiar texto para a área de transferência
  const copyToClipboard = (text: string, field: string) => {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        setCopiedField(field);
        setTimeout(() => setCopiedField(null), 2000);
      })
      .catch(() => {
        // silencioso
      });
  };

  useEffect(() => {
    fetchData();

    // Escutar notificações de pagamento confirmado
    const handlePaymentConfirmed = (_event: CustomEvent) => {
      // Atualizar lista de serviços após confirmação
      fetchData();
      setError('');
    };

    window.addEventListener('payment_confirmed', handlePaymentConfirmed as EventListener);

    return () => {
      window.removeEventListener('payment_confirmed', handlePaymentConfirmed as EventListener);
    };
  }, []);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      const servicesResponse = await api.get('/services');
      const servicesData = servicesResponse.data?.data?.services || [];
      setServices(servicesData);
    } catch (_err: any) {
      setError('Erro ao carregar dados');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchPlansForModal = async () => {
    try {
      setIsLoadingPlans(true);
      // Buscar produtos WHMCS apenas quando abrir o modal
      const productsResponse = await api.get('/services/whmcs-products');
      const productsData = productsResponse.data?.data?.products || [];

      // Converter produtos WHMCS para formato de planos para compatibilidade
      // Usar o local_plan.id se disponível, senão usar product.id
      const plansData: Plan[] = productsData.map((product: any) => ({
        id: product.local_plan?.id || product.id, // Usar ID do plano local se disponível
        name: product.name,
        description: product.description,
        price: product.local_plan?.price || parseFloat(product.pricing?.monthly || '0'),
        concurrent_calls: product.local_plan?.concurrent_calls || 1,
        monthly_calls: product.local_plan?.monthly_calls || null,
        whmcs_product_id: product.id, // Manter o ID do produto WHMCS
        is_whmcs_product: true,
        features: product.local_plan?.features || [],
      }));

      setPlans(plansData);
    } catch (err: any) {
      setError('Erro ao carregar planos');
    } finally {
      setIsLoadingPlans(false);
    }
  };

  const resetContractForm = () => {
    // Se necessário futuramente
  };

  const handleEditService = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedService) return;

    try {
      await api.put(`/services/${selectedService.id}`, {
        name: editService.name,
        description: editService.description,
      });

      setShowEditModal(false);
      setSelectedService(null);
      resetEditForm();
      fetchData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao atualizar serviço');
    }
  };

  const openEditModal = (service: Service) => {
    setSelectedService(service);
    setEditService({
      name: service.name,
      description: service.description || '',
    });
    setShowEditModal(true);
  };


  const resetEditForm = () => {
    setEditService({
      name: '',
      description: '',
    });
  };


  const openDeleteModal = (service: Service) => {
    setServiceToDelete(service);
    setDeleteConfirmationName('');
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setServiceToDelete(null);
    setDeleteConfirmationName('');
    setShowDeleteModal(false);
    setIsDeletingService(false);
  };

  const handleDeleteService = async () => {
    if (!serviceToDelete) return;

    if (deleteConfirmationName.trim() !== serviceToDelete.name.trim()) {
      setError('O nome digitado não confere com o nome do serviço');
      return;
    }

    try {
      setIsDeletingService(true);
      setError('');

      await api.delete(`/services/${serviceToDelete.id}`);

      closeDeleteModal();
      await fetchData();

      // Reset to first page if current page becomes empty
      const newTotalPages = Math.ceil((services.length - 1) / itemsPerPage);
      if (currentPage > newTotalPages && newTotalPages > 0) {
        setCurrentPage(newTotalPages);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao excluir serviço');
    } finally {
      setIsDeletingService(false);
    }
  };

  const getStatusIcon = (service: Service) => {
    const status = service.whmcs_status || service.status;

    switch (status) {
      case 'active':
        return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
      case 'inactive':
        return <XCircleIcon className="h-5 w-5 text-red-500" />;
      case 'suspended':
        return <AlertCircleIcon className="h-5 w-5 text-yellow-500" />;
      case 'pending':
        return <AlertCircleIcon className="h-5 w-5 text-blue-500" />;
      case 'terminated':
      case 'cancelled':
        return <XCircleIcon className="h-5 w-5 text-red-600" />;
      default:
        return <AlertCircleIcon className="h-5 w-5 text-gray-500" />;
    }
  };

  const getStatusText = (service: Service) => {
    // Verificar se a fatura foi reembolsada
    if (service.invoice_status === 'Refunded') {
      return 'Pagamento Reembolsado';
    }

    if (service.whmcs_status === 'active' && service.status === 'inactive') {
      return 'Aguardando Configuração';
    }

    const status = service.whmcs_status || service.status;

    switch (status) {
      case 'active':
        return 'Ativo';
      case 'inactive':
        return 'Inativo';
      case 'suspended':
        return 'Suspenso';
      case 'pending':
        return 'Aguardando Pagamento';
      case 'terminated':
        return 'Encerrado';
      case 'cancelled':
        return 'Cancelado';
      default:
        return 'Desconhecido';
    }
  };

  const getStatusColor = (service: Service) => {
    // Verificar se a fatura foi reembolsada
    if (service.invoice_status === 'Refunded') {
      return 'bg-orange-100 text-orange-800 border-orange-200';
    }

    if (service.whmcs_status === 'active' && service.status === 'inactive') {
      return 'bg-amber-100 text-amber-800 border-amber-200';
    }

    const status = service.whmcs_status || service.status;

    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'inactive':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'suspended':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'pending':
        return 'bg-amber-100 text-amber-800 border-amber-200';
      case 'terminated':
      case 'cancelled':
        return 'bg-red-100 text-red-900 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const handlePayInvoice = async (service: Service) => {
    if (!service.whmcs_invoice_id) {
      setError('Fatura não encontrada para este serviço');
      return;
    }
    navigate(`/invoice/${service.whmcs_invoice_id}`);
  };

  const handleConfigureService = (service: Service) => {
    setSelectedService(service);
    setShowConfigureModal(true);
  };

  const togglePasswordVisibility = (serviceId: number) => {
    setShowPasswordFor(showPasswordFor === serviceId ? null : serviceId);
  };

  const handleSyncService = async (service: Service) => {
    try {
      setSyncingId(service.id);
      setError('');
      await api.post(`/services/${service.id}/sync`);
      await fetchData();
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.error || err.response?.data?.message || 'Erro ao sincronizar serviço';
      setError(errorMessage);
    } finally {
      setSyncingId(null);
    }
  };

  const handleCheckMetaStatus = async (service: Service) => {
    try {
      setCheckingMetaStatus(service.id);
      setError('');

      const response = await api.post(`/services/${service.id}/check-meta-status`);

      setMetaStatus(prev => ({
        ...prev,
        [service.id]: {
          configured: response.data.configured,
          message: response.data.message,
          details: response.data.details
        }
      }));

      // Mostrar feedback ao usuário
      import('react-hot-toast').then(({ default: toast }) => {
        if (response.data.configured) {
          toast.success('Meta API configurada corretamente! ✅');
        } else {
          toast.error(response.data.message || 'Meta API não configurada');
        }
      });

    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Erro ao verificar status da Meta API';
      setError(errorMessage);

      setMetaStatus(prev => ({
        ...prev,
        [service.id]: {
          configured: false,
          message: errorMessage
        }
      }));

      import('react-hot-toast').then(({ default: toast }) => {
        toast.error(errorMessage);
      });
    } finally {
      setCheckingMetaStatus(null);
    }
  };

  const handleCheckBusinessHoursStatus = async (service: Service) => {
    try {
      setCheckingBusinessHoursStatus(service.id);
      setError('');

      // First check if Meta API is configured
      const metaResponse = await api.post(`/services/${service.id}/check-meta-status`);

      if (!metaResponse.data.configured) {
        // If Meta API is not configured, show error message
        import('react-hot-toast').then(({ default: toast }) => {
          toast.error('Meta API não configurada. Entre em contato com o administrador.');
        });
        return;
      }

      // If Meta API is configured, fetch business hours data first
      const businessHoursResponse = await api.get(`/services/${service.id}/business-hours`);

      // Store the preloaded data
      setPreloadedBusinessHours(prev => ({
        ...prev,
        [service.id]: businessHoursResponse.data?.data?.business_hours || {}
      }));

      // Update both statuses
      setMetaStatus(prev => ({
        ...prev,
        [service.id]: {
          configured: true,
          message: 'Meta API validada ✓'
        }
      }));
      setBusinessHoursStatus(prev => ({
        ...prev,
        [service.id]: {
          configured: true,
          message: 'Horário comercial validado ✓'
        }
      }));

      // Open modal with preloaded data
      setSelectedService(service);
      setShowBusinessHoursModal(true);

    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Erro ao verificar status do horário comercial';
      setError(errorMessage);

      setBusinessHoursStatus(prev => ({
        ...prev,
        [service.id]: {
          configured: false,
          message: errorMessage
        }
      }));

      import('react-hot-toast').then(({ default: toast }) => {
        toast.error(errorMessage);
      });
    } finally {
      setCheckingBusinessHoursStatus(null);
    }
  };

  const handleConfigureBusinessHours = async (service: Service) => {
    try {
      setCheckingBusinessHoursStatus(service.id);
      setError('');

      // First check Meta API status
      const metaResponse = await api.post(`/services/${service.id}/check-meta-status`);

      if (!metaResponse.data.configured) {
        import('react-hot-toast').then(({ default: toast }) => {
          toast.error('Configure a Meta API primeiro');
        });
        return;
      }

      // If Meta API is configured, fetch business hours data first
      const businessHoursResponse = await api.get(`/services/${service.id}/business-hours`);

      // Store the preloaded data
      setPreloadedBusinessHours(prev => ({
        ...prev,
        [service.id]: businessHoursResponse.data?.data?.business_hours || {}
      }));

      // Update both statuses to show as validated
      setMetaStatus(prev => ({
        ...prev,
        [service.id]: {
          configured: true,
          message: 'Meta API validada ✓'
        }
      }));
      setBusinessHoursStatus(prev => ({
        ...prev,
        [service.id]: {
          configured: true,
          message: 'Horário comercial validado ✓'
        }
      }));

      // Open modal with preloaded data
      setSelectedService(service);
      setShowBusinessHoursModal(true);

    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Erro ao carregar configuração de horário comercial';
      setError(errorMessage);

      import('react-hot-toast').then(({ default: toast }) => {
        toast.error(errorMessage);
      });
    } finally {
      setCheckingBusinessHoursStatus(null);
    }
  };

  const extractServerHostname = (url: string) => {
    try {
      const urlObj = new URL(url);
      return urlObj.hostname;
    } catch {
      return url.replace(/^https?:\/\//, '').replace(/\/.*$/, '');
    }
  };

  // Pagination logic
  const totalPages = Math.ceil(services.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentServices = services.slice(startIndex, endIndex);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const resetPagination = () => {
    setCurrentPage(1);
  };

  const formatDate = (dateString: string) => {
    try {
      if (!dateString) {
        return 'Data não disponível';
      }
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return 'Data não disponível';
      }
      return date.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
    } catch {
      return 'Data não disponível';
    }
  };

  // Handler para contratação via modal
  const handleContract = async (
    planId: number,
    serviceName: string,
    serviceDescription: string,
    billingCycle: string,
    paymentMethod: string
  ) => {
    try {
      setIsCreatingService(true);
      setError('');

      const requestData = {
        plan_id: planId,
        billing_cycle: billingCycle,
        payment_method: paymentMethod,
        service_name: serviceName,
        service_description: serviceDescription,
      };

      await api.post('/orders/create', requestData);

      // Fechar modal e resetar formulário
      setShowCreateModal(false);
      resetContractForm();

      // Reset pagination and update the list after a short delay
      setTimeout(async () => {
        try {
          await fetchData();
          resetPagination();
        } catch {
          // silencioso
        }
      }, 1000);
    } catch (err: any) {
      const errorData = err.response?.data;
      let errorMessage = 'Erro ao contratar serviço';
      if (errorData) {
        errorMessage = errorData.message || errorData.error || errorMessage;
      }

      // Usar toast notification em vez de erro na página
      import('react-hot-toast').then(({ default: toast }) => {
        toast.error(errorMessage);
      });
    } finally {
      setIsCreatingService(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div>
      {/* Elegant Header */}
      <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 mx-6 mt-6 overflow-hidden">
        <div className="px-8 py-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-2xl flex items-center justify-center mr-4 shadow-lg">
                <PhoneIcon className="h-6 w-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  Serviços VoIP
                </h1>
                <p className="text-sm text-gray-600 mt-1">
                  Gerencie seus ramais e extensões
                </p>
              </div>
            </div>

            <div className="flex-shrink-0">
              <button
                onClick={async () => {
                  await fetchPlansForModal();
                  setShowCreateModal(true);
                }}
                disabled={isLoadingPlans}
                className="inline-flex items-center px-6 py-3 border border-transparent text-sm font-semibold rounded-2xl shadow-lg text-white bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 hover:shadow-xl hover:transform hover:scale-105"
              >
                {isLoadingPlans ? (
                  <>
                    <RefreshCwIcon className="h-5 w-5 mr-2 animate-spin" />
                    Carregando...
                  </>
                ) : (
                  <>
                    <PlusIcon className="h-5 w-5 mr-2" />
                    Contratar Serviço
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div className="mx-6 mb-6">
          <div className="bg-gradient-to-r from-red-50 to-pink-50 border border-red-200/60 rounded-2xl p-6 shadow-lg">
            <div className="flex items-center">
              <div className="w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center mr-4">
                <AlertCircleIcon className="h-5 w-5 text-red-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-red-800 mb-1">Erro ao carregar dados</h3>
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Services Content */}
      <div className="mx-6">
        {services.length === 0 ? (
          <div className="bg-gray-50 rounded-2xl border border-gray-200/60 p-12 text-center">
            <div className="w-16 h-16 bg-gradient-to-br from-cyan-100 to-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <PhoneIcon className="h-8 w-8 text-cyan-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Nenhum serviço encontrado</h3>
            <p className="text-gray-600 text-sm mb-6 max-w-sm mx-auto">
              Comece criando seu primeiro ramal VoIP. É rápido e totalmente automatizado.
            </p>
            <button
              onClick={async () => {
                await fetchPlansForModal();
                setShowCreateModal(true);
              }}
              disabled={isLoadingPlans}
              className="inline-flex items-center px-6 py-3 border border-transparent text-sm font-medium rounded-lg shadow-sm text-white bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
            >
              {isLoadingPlans ? (
                <>
                  <RefreshCwIcon className="h-4 w-4 mr-2 animate-spin" />
                  Carregando...
                </>
              ) : (
                <>
                  <PlusIcon className="h-4 w-4 mr-2" />
                  Contratar Primeiro Serviço
                </>
              )}
            </button>
          </div>
        ) : (
          <>
            {/* Services Grid */}
            <div className="grid gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 mt-6">
              {currentServices.map((service) => (
                <div key={service.id} className="bg-white rounded-2xl shadow-lg border border-gray-200/60 overflow-hidden hover:shadow-xl transition-all duration-300 hover:transform hover:scale-[1.01]">
                  {/* Service Header - Identity Only */}
                  <div className="bg-gradient-to-r from-cyan-500 to-blue-600 p-4">
                    <div className="flex items-center">
                      <PhoneIcon className="h-5 w-5 text-white mr-3 flex-shrink-0" />
                      <div className="min-w-0 flex-1">
                        <h3 className="text-sm font-bold text-white truncate" title={service.name}>
                          {service.name}
                        </h3>
                        <p className="text-white/80 text-xs">Ramal {service.extension || '—'}</p>
                      </div>
                    </div>
                  </div>


                  {/* Service Content */}
                  <div className="p-4">
                    {/* Metadados organizados - linhas horizontais consistentes */}
                    <div className="space-y-2 mb-5">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-medium text-gray-600">Plano:</span>
                        <span className="text-xs font-semibold text-gray-900 truncate ml-2" title={service.plan?.name}>
                          {service.plan?.name || '—'}
                        </span>
                      </div>

                      <div className="flex items-center justify-between">
                        <span className="text-xs font-medium text-gray-600">Chamadas:</span>
                        <span className="text-xs font-semibold text-gray-900 ml-2">
                          {typeof service.plan?.concurrent_calls === 'number'
                            ? `${service.plan.concurrent_calls}`
                            : '—'}
                        </span>
                      </div>

                      <div className="flex items-center justify-between">
                        <span className="text-xs font-medium text-gray-600">Cobrança:</span>
                        <span className="text-xs font-semibold text-gray-900 ml-2">
                          {service.billing_cycle === 'monthly'
                            ? 'Mensal'
                            : service.billing_cycle === 'quarterly'
                            ? 'Trimestral'
                            : service.billing_cycle === 'semiannually'
                            ? 'Semestral'
                            : service.billing_cycle === 'annually'
                            ? 'Anual'
                            : '—'}
                        </span>
                      </div>

                      <div className="flex items-center justify-between">
                        <span className="text-xs font-medium text-gray-600">Próximo Vencimento:</span>
                        <span className="text-xs font-semibold text-gray-900 ml-2">
                          {service.next_due_date
                            ? formatDate(service.next_due_date)
                            : '—'}
                        </span>
                      </div>
                    </div>

                    {/* Service Description */}
                    {service.description && (
                      <div className="bg-gray-50 rounded-lg p-3 border border-gray-200/60">
                        <p className="text-xs text-gray-700 leading-relaxed">
                          {service.description}
                        </p>
                      </div>
                    )}

                    {/* Ação contextual com contexto */}

                    {service.whmcs_status === 'pending' && service.whmcs_invoice_id && service.invoice_status !== 'Paid' && service.invoice_status !== 'paid' && service.invoice_status !== 'PAID' && (
                      <div className="mb-3">
                        <button
                          onClick={() => handlePayInvoice(service)}
                          className="w-full inline-flex items-center justify-center px-3 py-2.5 border border-transparent text-sm font-medium rounded-lg shadow-sm text-white bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200"
                        >
                          💳 Pagar Fatura
                        </button>
                      </div>
                    )}

                    {((service.whmcs_status === 'active' && service.status === 'inactive' && service.extension) ||
                      (service.whmcs_status === 'pending' && service.status === 'inactive' && service.extension && service.invoice_status === 'Paid' && service.phone_id && service.meta_api_key)) && (
                      <div className="mb-3">
                        <button
                          onClick={() => handleConfigureService(service)}
                          className="w-full inline-flex items-center justify-center px-3 py-2.5 border border-transparent text-sm font-medium rounded-lg shadow-sm text-white bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-all duration-200"
                        >
                          ⚙️ Configurar Ramal
                        </button>
                      </div>
                    )}

                    {/* Status único - apenas para estados especiais */}
                    {service.whmcs_status === 'pending' && service.invoice_status !== 'Paid' && service.invoice_status !== 'paid' && service.invoice_status !== 'PAID' && (
                      <div className="bg-amber-50 rounded-lg p-3 border border-amber-200/60 mt-2">
                        <div className="flex items-start">
                          <AlertCircleIcon className="h-4 w-4 text-amber-600 mr-2 mt-0.5 flex-shrink-0" />
                          <div>
                            <p className="text-xs font-semibold text-amber-800 mb-1">Aguardando Pagamento</p>
                            <p className="text-xs text-amber-700 leading-relaxed">
                              Serviço será ativado após confirmação do pagamento.
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    {((service.whmcs_status === 'active' && service.status === 'inactive' && service.extension) ||
                      (service.whmcs_status === 'pending' && service.status === 'inactive' && service.extension && service.invoice_status === 'Paid' && service.phone_id && service.meta_api_key)) && (
                      <div className="bg-amber-50 rounded-lg p-3 border border-amber-200/60 mt-2">
                        <div className="flex items-start">
                          <AlertCircleIcon className="h-4 w-4 text-amber-600 mr-2 mt-0.5 flex-shrink-0" />
                          <div>
                            <p className="text-xs font-semibold text-amber-800 mb-1">Aguardando Configuração</p>                        
                          </div>
                        </div>
                      </div>
                    )}

                    {service.invoice_status === 'Refunded' && (
                      <div className="bg-orange-50 rounded-lg p-3 border border-orange-200/60 mt-2">
                        <div className="flex items-start">
                          <XCircleIcon className="h-4 w-4 text-orange-600 mr-2 mt-0.5 flex-shrink-0" />
                          <div>
                            <p className="text-xs font-semibold text-orange-800 mb-1">Pagamento Reembolsado</p>
                            <p className="text-xs text-orange-700 leading-relaxed">
                              O pagamento desta fatura foi reembolsado. Entre em contato com o suporte para mais informações.
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Detalhes técnicos - apenas quando ativo */}
                    {service.status === 'active' && service.whmcs_status !== 'pending' && (
                      <div className="pt-3 border-t border-gray-100 space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-medium text-gray-600">Ramal:</span>
                          <div className="flex items-center space-x-1">
                            <span className="text-xs font-mono text-gray-900 break-all">{service.extension || '—'}</span>
                            {service.extension && (
                              <button
                                onClick={() => copyToClipboard(service.extension, `ramal-${service.id}`)}
                                className="text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0"
                                title="Copiar ramal"
                              >
                                {copiedField === `ramal-${service.id}` ? (
                                  <CheckIcon className="h-3 w-3 text-green-500" />
                                ) : (
                                  <CopyIcon className="h-3 w-3" />
                                )}
                              </button>
                            )}
                          </div>
                        </div>

                        <div className="flex items-center justify-between">
                          <span className="text-xs font-medium text-gray-600">Senha:</span>
                          <div className="flex items-center space-x-1">
                            <span className="text-xs font-mono text-gray-900">
                              {service.password
                                ? showPasswordFor === service.id
                                  ? service.password
                                  : '••••••••'
                                : '—'}
                            </span>
                            {service.password && (
                              <>
                                <button
                                  onClick={() => togglePasswordVisibility(service.id)}
                                  className="text-gray-400 hover:text-gray-600 transition-colors"
                                  title="Mostrar/ocultar senha"
                                >
                                  {showPasswordFor === service.id ? (
                                    <EyeOffIcon className="h-3 w-3" />
                                  ) : (
                                    <EyeIcon className="h-3 w-3" />
                                  )}
                                </button>
                                {showPasswordFor === service.id && (
                                  <button
                                    onClick={() => copyToClipboard(service.password, `senha-${service.id}`)}
                                    className="text-gray-400 hover:text-gray-600 transition-colors"
                                    title="Copiar senha"
                                  >
                                    {copiedField === `senha-${service.id}` ? (
                                      <CheckIcon className="h-3 w-3 text-green-500" />
                                    ) : (
                                      <CopyIcon className="h-3 w-3" />
                                    )}
                                  </button>
                                )}
                              </>
                            )}
                          </div>
                        </div>

                        <div className="flex items-center justify-between">
                          <span className="text-xs font-medium text-gray-600">Servidor:</span>
                          <div className="flex items-center space-x-1">
                            <span className="text-xs text-gray-900 break-all">{extractServerHostname(service.server?.url || '') || '—'}</span>
                            {service.server?.url && (
                              <button
                                onClick={() => copyToClipboard(
                                  extractServerHostname(service.server?.url || ''),
                                  `servidor-${service.id}`
                                )}
                                className="text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0"
                                title="Copiar servidor"
                              >
                                {copiedField === `servidor-${service.id}` ? (
                                  <CheckIcon className="h-3 w-3 text-green-500" />
                                ) : (
                                  <CopyIcon className="h-3 w-3" />
                                )}
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Meta API Status Indicator */}
                    {service.status === 'active' && service.phone_id && service.meta_api_key && (
                      <div className="pt-2 border-t border-gray-100">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs font-medium text-gray-600">Meta API:</span>
                          <div className="flex items-center space-x-2">
                            {metaStatus[service.id] ? (
                              <div className="flex items-center space-x-1">
                                {metaStatus[service.id].configured ? (
                                  <CheckCircleIcon className="h-3 w-3 text-green-500" />
                                ) : (
                                  <XCircleIcon className="h-3 w-3 text-red-500" />
                                )}
                                <span className={`text-xs font-medium ${
                                  metaStatus[service.id].configured ? 'text-green-700' : 'text-red-700'
                                }`}>
                                  {metaStatus[service.id].configured ? 'OK' : 'Erro'}
                                </span>
                              </div>
                            ) : (
                              <span className="text-xs text-gray-500">Não verificado</span>
                            )}
                            <button
                              onClick={() => {
                                if (metaStatus[service.id] && !metaStatus[service.id].configured) {
                                  // If status was checked and is error, show message to contact admin
                                  import('react-hot-toast').then(({ default: toast }) => {
                                    toast.error('Meta API com erro. Entre em contato com o administrador.');
                                  });
                                } else {
                                  // If not checked or status is OK, check status
                                  handleCheckMetaStatus(service);
                                }
                              }}
                              disabled={checkingMetaStatus === service.id}
                              className={`inline-flex items-center justify-center px-1.5 py-1 border border-gray-300 text-xs font-medium rounded shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-cyan-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all ${
                                checkingMetaStatus === service.id ? 'px-2' : ''
                              }`}
                              title={
                                checkingMetaStatus === service.id
                                  ? "Verificando Meta API..."
                                  : metaStatus[service.id] && !metaStatus[service.id].configured
                                  ? "Configurar Meta API"
                                  : "Verificar status da Meta API"
                              }
                            >
                              {checkingMetaStatus === service.id ? (
                                <>
                                  <RefreshCwIcon className="h-3 w-3 animate-spin mr-1" />
                                  <span className="text-xs">Verificando...</span>
                                </>
                              ) : metaStatus[service.id] && !metaStatus[service.id].configured ? (
                                <SettingsIcon className="h-3 w-3" />
                              ) : (
                                <RefreshCwIcon className="h-3 w-3" />
                              )}
                            </button>
                          </div>
                        </div>
                        {metaStatus[service.id] && !metaStatus[service.id].configured && (
                          <div className="bg-red-50 rounded-lg p-2 border border-red-200/60">
                            <p className="text-xs text-red-700 leading-relaxed mb-2">
                              {metaStatus[service.id].message}
                            </p>
                            <button
                              onClick={() => {
                                import('react-hot-toast').then(({ default: toast }) => {
                                  toast.error('Para reconfigurar a Meta API, entre em contato com o administrador.');
                                });
                              }}
                              className="inline-flex items-center px-2 py-1 border border-transparent text-xs font-medium rounded shadow-sm text-white bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 focus:outline-none focus:ring-1 focus:ring-blue-500 transition-all"
                            >
                              🔧 Contatar Admin
                            </button>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Business Hours Status Indicator */}
                    {service.status === 'active' && service.phone_id && service.meta_api_key && (
                      <div className="pt-2 border-t border-gray-100">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs font-medium text-gray-600">Horário Comercial:</span>
                          <div className="flex items-center space-x-2">
                            {businessHoursStatus[service.id] ? (
                              <div className="flex items-center space-x-1">
                                {businessHoursStatus[service.id].configured ? (
                                  <CheckCircleIcon className="h-3 w-3 text-green-500" />
                                ) : (
                                  <XCircleIcon className="h-3 w-3 text-red-500" />
                                )}
                                <span className={`text-xs font-medium ${
                                  businessHoursStatus[service.id].configured ? 'text-green-700' : 'text-red-700'
                                }`}>
                                  {businessHoursStatus[service.id].configured ? 'OK' : 'Pendente'}
                                </span>
                              </div>
                            ) : (
                              <span className="text-xs text-gray-500">Não verificado</span>
                            )}
                            <button
                              onClick={() => {
                                if (businessHoursStatus[service.id] && !businessHoursStatus[service.id].configured) {
                                  // If status was checked and is error, open config modal
                                  handleConfigureBusinessHours(service);
                                } else {
                                  // If not checked or status is OK, check status
                                  handleCheckBusinessHoursStatus(service);
                                }
                              }}
                              disabled={checkingBusinessHoursStatus === service.id}
                              className={`inline-flex items-center justify-center px-1.5 py-1 border border-gray-300 text-xs font-medium rounded shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-cyan-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all ${
                                checkingBusinessHoursStatus === service.id ? 'px-2' : ''
                              }`}
                              title={
                                checkingBusinessHoursStatus === service.id
                                  ? "Verificando horário comercial..."
                                  : businessHoursStatus[service.id] && !businessHoursStatus[service.id].configured
                                  ? "Configurar horário comercial"
                                  : "Verificar status do horário comercial"
                              }
                            >
                              {checkingBusinessHoursStatus === service.id ? (
                                <>
                                  <RefreshCwIcon className="h-3 w-3 animate-spin mr-1" />
                                  <span className="text-xs">Verificando...</span>
                                </>
                              ) : businessHoursStatus[service.id] && !businessHoursStatus[service.id].configured ? (
                                <SettingsIcon className="h-3 w-3" />
                              ) : (
                                <RefreshCwIcon className="h-3 w-3" />
                              )}
                            </button>
                          </div>
                        </div>
                        {businessHoursStatus[service.id] && !businessHoursStatus[service.id].configured && (
                          <div className="bg-red-50 rounded-lg p-2 border border-red-200/60">
                            <p className="text-xs text-red-700 leading-relaxed mb-2">
                              {businessHoursStatus[service.id].message}
                            </p>
                            <button
                              onClick={() => handleConfigureBusinessHours(service)}
                              className="inline-flex items-center px-2 py-1 border border-transparent text-xs font-medium rounded shadow-sm text-white bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 focus:outline-none focus:ring-1 focus:ring-blue-500 transition-all"
                            >
                              🕒 Configurar Horário
                            </button>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Action Buttons */}
                    <div className="pt-3 border-t border-gray-100">
                      <div className="flex flex-wrap gap-2 justify-end">
                        {service.whmcs_status === 'pending' && service.invoice_status !== 'Paid' && service.invoice_status !== 'paid' && service.invoice_status !== 'PAID' ? (
                          <button
                            onClick={() => openDeleteModal(service)}
                            className="inline-flex items-center justify-center px-2 py-2 border border-transparent text-sm font-medium rounded-lg shadow-sm text-white bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-all duration-200"
                            title="Excluir serviço"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        ) : (service.status === 'active' && service.whmcs_status === 'active') ? (
                          <>
                            <button
                              onClick={() => openEditModal(service)}
                              className="inline-flex items-center justify-center px-2 py-2 border border-gray-300 text-sm font-medium rounded-lg shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500 transition-all duration-200"
                              title="Editar serviço"
                            >
                              <EditIcon className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleSyncService(service)}
                              disabled={syncingId === service.id}
                              className="inline-flex items-center justify-center px-2 py-2 border border-transparent text-sm font-medium rounded-lg shadow-sm text-white bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                              title="Sincronizar serviço"
                            >
                              {syncingId === service.id ? (
                                <RefreshCwIcon className="h-4 w-4 animate-spin" />
                              ) : (
                                <RefreshCwIcon className="h-4 w-4" />
                              )}
                            </button>
                            <button
                              onClick={() => openDeleteModal(service)}
                              className="inline-flex items-center justify-center px-2 py-2 border border-transparent text-sm font-medium rounded-lg shadow-sm text-white bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-all duration-200"
                              title="Excluir serviço"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          </>
                        ) : (
                          <button
                            onClick={() => openDeleteModal(service)}
                            className="inline-flex items-center justify-center px-2 py-2 border border-transparent text-sm font-medium rounded-lg shadow-sm text-white bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-all duration-200"
                            title="Excluir serviço"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center mt-8">
                <div className="flex items-center space-x-2">
                  {/* Previous Button */}
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="p-2 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    title="Página anterior"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>

                  {/* Page Numbers */}
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                    <button
                      key={page}
                      onClick={() => handlePageChange(page)}
                      className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                        page === currentPage
                          ? 'bg-cyan-600 text-white'
                          : 'border border-gray-300 text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      {page}
                    </button>
                  ))}

                  {/* Next Button */}
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="p-2 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    title="Próxima página"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>
              </div>
            )}
          </>
        )}
        </div>

        {/* Contract Service Modal */}
      <ContractServiceModal
        isOpen={showCreateModal}
        onClose={() => {
          setShowCreateModal(false);
          resetContractForm();
        }}
        plans={plans}
        onContract={handleContract}
        isLoading={isCreatingService}
        isLoadingPlans={isLoadingPlans}
      />

      {/* Edit Service Modal */}
      {showEditModal && selectedService && (
        <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-center justify-center p-4">
          <div className="w-full max-w-md max-h-[90vh] bg-gradient-to-br from-white to-gray-50 rounded-2xl shadow-2xl border border-gray-200 overflow-hidden">
            <div className="overflow-y-auto p-6 max-h-[90vh]">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-1">Editar Serviço</h3>
                  <p className="text-gray-600 text-sm">Atualize as configurações do seu ramal</p>
                </div>
                <button
                  onClick={() => setShowEditModal(false)}
                  className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 p-2 rounded-full transition-colors"
                >
                  <XCircleIcon className="h-6 w-6" />
                </button>
              </div>
              <form onSubmit={handleEditService} className="space-y-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Nome do Serviço</label>
                  <input
                    type="text"
                    required
                    className="block w-full border-2 border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition-all bg-gray-50 focus:bg-white"
                    value={editService.name}
                    onChange={(e) => setEditService({ ...editService, name: e.target.value })}
                    placeholder="Nome do serviço"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Descrição</label>
                  <textarea
                    className="block w-full border-2 border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition-all bg-gray-50 focus:bg-white"
                    value={editService.description}
                    onChange={(e) => setEditService({ ...editService, description: e.target.value })}
                    placeholder="Descrição opcional do serviço"
                    rows={3}
                  />
                </div>

                <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => setShowEditModal(false)}
                    className="px-6 py-3 border-2 border-gray-300 rounded-xl text-sm font-semibold text-gray-700 hover:bg-gray-50 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500 transition-all"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="px-6 py-3 border border-transparent rounded-xl shadow-lg text-sm font-semibold text-white bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500 transition-all transform hover:scale-105"
                  >
                    Salvar Alterações
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Configure Service Modal */}
      <ConfigureServiceModal
        isOpen={showConfigureModal}
        onClose={() => {
          setShowConfigureModal(false);
          setSelectedService(null);
        }}
        service={selectedService}
        onConfigured={() => {
          setShowConfigureModal(false);
          setSelectedService(null);
          // Pequeno delay para garantir que o backend tenha processado as mudanças
          setTimeout(() => {
            fetchData();
          }, 1000);
        }}
      />

      {/* Configure Business Hours Modal */}
      <ConfigureBusinessHoursModal
        isOpen={showBusinessHoursModal}
        onClose={() => {
          setShowBusinessHoursModal(false);
          setSelectedService(null);
        }}
        service={selectedService}
        onConfigured={() => {
          setShowBusinessHoursModal(false);
          setSelectedService(null);
          // Update business hours status after configuration
          if (selectedService) {
            setBusinessHoursStatus(prev => ({
              ...prev,
              [selectedService.id]: {
                configured: true,
                message: 'Horário comercial configurado'
              }
            }));
          }
          // Pequeno delay para garantir que o backend tenha processado as mudanças
          setTimeout(() => {
            fetchData();
          }, 1000);
        }}
        skipMetaValidation={true}
        preloadedData={selectedService ? preloadedBusinessHours[selectedService.id] : undefined}
      />



      {/* Delete Confirmation Modal */}
      {showDeleteModal && serviceToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-center justify-center p-4">
          <div className="w-full max-w-md bg-gradient-to-br from-white to-gray-50 rounded-2xl shadow-2xl border border-gray-200">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-2xl font-bold text-red-600 mb-1">⚠️ Excluir Serviço</h3>
                  <p className="text-gray-600 text-sm">Esta ação é irreversível</p>
                </div>
                <button
                  onClick={closeDeleteModal}
                  disabled={isDeletingService}
                  className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 p-2 rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <XCircleIcon className="h-6 w-6" />
                </button>
              </div>

              <div className="mb-6">
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                  <div className="flex items-start">
                    <AlertCircleIcon className="h-5 w-5 text-red-500 mt-0.5 mr-3 flex-shrink-0" />
                    <div>
                      <h4 className="text-sm font-semibold text-red-800 mb-2">Atenção: Ação Irreversível</h4>
                      <ul className="text-xs text-red-700 space-y-1">
                        <li>• O serviço será permanentemente excluído</li>
                        <li>• O ramal será removido do servidor</li>
                        <li>• O valor financeiro NÃO será reaproveitado</li>
                        <li>• Todas as configurações serão perdidas</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-100 rounded-lg p-3 mb-4">
                  <p className="text-sm text-gray-700 mb-1">
                    <strong>Serviço a ser excluído:</strong>
                  </p>
                  <p className="text-lg font-semibold text-gray-900">{serviceToDelete.name}</p>
                  <p className="text-sm text-gray-600">Ramal: {serviceToDelete.extension}</p>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Para confirmar, digite o nome do serviço exatamente como aparece acima:
                  </label>
                  <input
                    type="text"
                    disabled={isDeletingService}
                    className="block w-full border-2 border-gray-300 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all bg-white disabled:opacity-50 disabled:cursor-not-allowed"
                    value={deleteConfirmationName}
                    onChange={(e) => setDeleteConfirmationName(e.target.value)}
                    placeholder={`Digite "${serviceToDelete.name}" para confirmar`}
                  />
                  {deleteConfirmationName &&
                    deleteConfirmationName.trim() !== serviceToDelete.name.trim() && (
                      <p className="text-xs text-red-600 mt-1">
                        O nome digitado não confere. Digite exatamente: "{serviceToDelete.name}"
                      </p>
                    )}
                </div>
              </div>

              <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
                <button
                  type="button"
                  onClick={closeDeleteModal}
                  disabled={isDeletingService}
                  className="px-6 py-3 border-2 border-gray-300 rounded-xl text-sm font-semibold text-gray-700 hover:bg-gray-50 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleDeleteService}
                  disabled={deleteConfirmationName.trim() !== serviceToDelete.name.trim() || isDeletingService}
                  className="px-6 py-3 border border-transparent rounded-xl shadow-lg text-sm font-semibold text-white bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all transform hover:scale-105 disabled:transform-none flex items-center"
                >
                  {isDeletingService ? (
                    <>
                      <RefreshCwIcon className="h-4 w-4 mr-2 animate-spin" />
                      Excluindo...
                    </>
                  ) : (
                    'Excluir Permanentemente'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ServicesPage;
